import type { RequestFn } from '../http';

export type GoodsReceiptStatus = 'DRAFT' | 'COMPLETED' | 'POSTED' | 'CANCELLED';
export type ItemCondition = 'NEW' | 'USED' | 'RMA' | 'DEFECT';
export type AttachmentType = 'PHOTO' | 'SIGNATURE' | 'DELIVERY_NOTE';

export interface GoodsReceiptItem {
  id: string;
  articleId?: string | null;
  binRefId?: string | null;
  cwProductId: number | null;
  productName: string;
  partNumber: string | null;
  serialNumber: string | null;
  quantity: number;
  condition: ItemCondition;
  assetTag: string | null;
  macAddress: string | null;
  imei: string | null;
  binId: number | null;
  binName: string | null;
  notes: string | null;
  cwAdjustmentDetailId: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface GoodsReceiptAttachment {
  id: string;
  itemId: string | null;
  type: string;
  filename: string;
  mimeType: string;
  size: number;
  createdAt: string;
}

export interface GoodsReceiptDetail {
  id: string;
  status: GoodsReceiptStatus;
  supplierName: string | null;
  deliveryNoteNo: string | null;
  orderReference: string | null;
  cwPurchaseOrderId: number | null;
  warehouseId: number;
  warehouseName: string;
  binId: number | null;
  binName: string | null;
  signatureImage: string | null;
  signedByName: string | null;
  signedAt: string | null;
  notes: string | null;
  cwAdjustmentId: number | null;
  postedAt: string | null;
  postedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: { id: string; displayName: string };
  items: GoodsReceiptItem[];
  attachments: GoodsReceiptAttachment[];
  weclappPurchaseOrderId: string | null;
  weclappTicketId: string | null;
  weclappTicketNumber: string | null;
  technicianRequestId: string | null;
  purchaseOrderId: string | null;
}

export interface GoodsReceiptListItem {
  id: string;
  status: GoodsReceiptStatus;
  supplierName: string | null;
  deliveryNoteNo: string | null;
  warehouseId: number;
  warehouseName: string;
  binName: string | null;
  itemCount: number;
  totalQuantity: number;
  createdAt: string;
  updatedAt: string;
  postedAt: string | null;
  createdByName: string;
}

export interface CreateGoodsReceiptInput {
  // Native Lager-Referenzen (ServiceHub) — bevorzugt
  warehouseRefId?: string;
  binRefId?: string;
  // Legacy CW-Referenz (optional)
  warehouseId?: number;
  warehouseName: string;
  binId?: number;
  binName?: string;
  supplierName?: string;
  deliveryNoteNo?: string;
  orderReference?: string;
  cwPurchaseOrderId?: number;
  purchaseOrderId?: string | null;
  notes?: string;
}

export interface UpdateGoodsReceiptInput {
  warehouseId?: number;
  warehouseName?: string;
  binId?: number | null;
  binName?: string | null;
  supplierName?: string | null;
  deliveryNoteNo?: string | null;
  orderReference?: string | null;
  cwPurchaseOrderId?: number | null;
  notes?: string | null;
  signatureImage?: string | null;
  signedByName?: string | null;
  weclappPurchaseOrderId?: string | null;
  weclappTicketId?: string | null;
  weclappTicketNumber?: string | null;
  technicianRequestId?: string | null;
  purchaseOrderId?: string | null;
}

export interface GoodsReceiptItemInput {
  // Native Artikel-/Bin-Referenz (ServiceHub) — bevorzugt
  articleId?: string | null;
  binRefId?: string | null;
  cwProductId?: number | null;
  productName: string;
  partNumber?: string | null;
  serialNumber?: string | null;
  quantity: number;
  condition: ItemCondition;
  assetTag?: string | null;
  macAddress?: string | null;
  imei?: string | null;
  cwBinId?: number | null;
  binName?: string | null;
  notes?: string | null;
}

export interface PostToWarehouseResult {
  success: boolean;
  cwAdjustmentId?: number;
  cwAdjustmentIdentifier?: string;
  itemsPosted: number;
}

export interface OrderSuggestion {
  orderId: string;
  title: string;
  description: string;
  score: number;
  reasoning: string;
  status: string;
}

export interface OrderSuggestionsResult {
  suggestions: OrderSuggestion[];
  fallback: boolean;
  fallbackMessage?: string;
}

export interface UnpostableItemsResponse {
  error: string;
  unpostable: { id: string; productName: string }[];
}

export function createGoodsReceiptsApi(request: RequestFn) {
  return {
    async listGoodsReceipts(filters?: {
      status?: GoodsReceiptStatus;
      limit?: number;
      offset?: number;
    }) {
      const qs = new URLSearchParams();
      if (filters?.status) qs.set('status', filters.status);
      if (filters?.limit !== undefined) qs.set('limit', String(filters.limit));
      if (filters?.offset !== undefined) qs.set('offset', String(filters.offset));
      const query = qs.toString();
      return request<{ receipts: GoodsReceiptListItem[] }>(
        `/goods-receipts${query ? `?${query}` : ''}`
      );
    },

    async createGoodsReceipt(data: CreateGoodsReceiptInput) {
      return request<{ receipt: { id: string; status: GoodsReceiptStatus } }>(
        '/goods-receipts',
        { method: 'POST', body: JSON.stringify(data) }
      );
    },

    async getGoodsReceipt(id: string) {
      return request<{ receipt: GoodsReceiptDetail }>(`/goods-receipts/${id}`);
    },

    async updateGoodsReceipt(id: string, patch: UpdateGoodsReceiptInput) {
      return request<{ receipt: { id: string; status: GoodsReceiptStatus } }>(
        `/goods-receipts/${id}`,
        { method: 'PUT', body: JSON.stringify(patch) }
      );
    },

    async cancelGoodsReceipt(id: string) {
      return request<{ success: boolean }>(`/goods-receipts/${id}`, {
        method: 'DELETE',
      });
    },

    async addGoodsReceiptItem(receiptId: string, item: GoodsReceiptItemInput) {
      return request<{ item: GoodsReceiptItem }>(
        `/goods-receipts/${receiptId}/items`,
        { method: 'POST', body: JSON.stringify(item) }
      );
    },

    async updateGoodsReceiptItem(
      receiptId: string,
      itemId: string,
      patch: Partial<GoodsReceiptItemInput>
    ) {
      return request<{ item: GoodsReceiptItem }>(
        `/goods-receipts/${receiptId}/items/${itemId}`,
        { method: 'PUT', body: JSON.stringify(patch) }
      );
    },

    async removeGoodsReceiptItem(receiptId: string, itemId: string) {
      return request<{ success: boolean }>(
        `/goods-receipts/${receiptId}/items/${itemId}`,
        { method: 'DELETE' }
      );
    },

    async uploadGoodsReceiptPhoto(
      receiptId: string,
      file: File,
      options: { type?: 'PHOTO' | 'DELIVERY_NOTE'; itemId?: string } = {}
    ) {
      const formData = new FormData();
      formData.append('type', options.type ?? 'PHOTO');
      if (options.itemId) formData.append('itemId', options.itemId);
      formData.append('file', file);
      return request<{ attachment: GoodsReceiptAttachment }>(
        `/goods-receipts/${receiptId}/photos`,
        { method: 'POST', body: formData }
      );
    },

    async removeGoodsReceiptAttachment(receiptId: string, attachmentId: string) {
      return request<{ success: boolean }>(
        `/goods-receipts/${receiptId}/attachments/${attachmentId}`,
        { method: 'DELETE' }
      );
    },

    async completeGoodsReceipt(id: string) {
      return request<{ receipt: { id: string; status: GoodsReceiptStatus } }>(
        `/goods-receipts/${id}/complete`,
        { method: 'POST' }
      );
    },

    async postGoodsReceiptToWarehouse(id: string) {
      return request<PostToWarehouseResult>(
        `/goods-receipts/${id}/post-to-warehouse`,
        { method: 'POST' }
      );
    },

    async getOrderSuggestions(id: string) {
      return request<OrderSuggestionsResult>(
        `/goods-receipts/${id}/order-suggestions`
      );
    },

    async assignOrder(id: string, orderId: string | null) {
      return request<{ success: boolean }>(
        `/goods-receipts/${id}/assign-order`,
        { method: 'POST', body: JSON.stringify({ orderId }) }
      );
    },
  };
}
